import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { TabsContent } from "@/components/ui/tabs";
import { LoadingState } from '@/components/ui/LoadingState';
import { Eye, Search } from 'lucide-react';
import OrganizationCombobox from './OrganizationCombobox';

interface Organization {
  id: string;
  name: string;
}

interface AdminLeadRow {
  id: string;
  organization_id?: string | null;
  organization_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  job_title?: string | null;
  source?: string | null;
  created_at?: string | null;
  last_seen_at?: string | null;
  conversation_count?: number | null;
  visitor_profile_count?: number | null;
}

const LeadsTab: React.FC = () => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<AdminLeadRow[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const selectedOrg = searchParams.get('org') || 'all';
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [leadIdQuery, setLeadIdQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const perPage = 25;

  const setSelectedOrg = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next && next !== 'all') {
      params.set('org', next);
    } else {
      params.delete('org');
    }
    setSearchParams(params, { replace: false });
  };

  const handleFindLeadById = () => {
    const id = leadIdQuery.trim();
    if (!id) {
      toast.error('Please enter a lead ID');
      return;
    }
    navigate(`/admin/leads/${id}`);
  };

  const fetchOrganizations = async () => {
    try {
      const response = await get('/admin/organizations/');
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  };

  const fetchLeads = async (pageNum: number) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pageNum),
        per_page: String(perPage),
      });
      if (selectedOrg !== 'all') {
        params.set('organization_id', selectedOrg);
      }
      if (search.trim()) {
        params.set('search', search.trim());
      }
      const response = await get(`/admin/leads?${params.toString()}`);
      setLeads(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalLeads(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchLeads(page);
  }, [page, selectedOrg, search]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [selectedOrg, search]);

  const getDisplayName = (row: AdminLeadRow): string => {
    const parts = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
    if (parts) return parts;
    if (row.email) return row.email;
    return 'Unknown contact';
  };

  const columns: ColumnDef<AdminLeadRow>[] = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => row.original.email || 'N/A',
    },
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => getDisplayName(row.original),
    },
    {
      accessorKey: 'company',
      header: 'Company',
      cell: ({ row }) => row.original.company || '—',
    },
    {
      accessorKey: 'organization_name',
      header: 'Organization',
      cell: ({ row }) => row.original.organization_name || 'N/A',
    },
    {
      accessorKey: 'conversation_count',
      header: 'Conversations',
      cell: ({ row }) => row.original.conversation_count ?? 0,
    },
    {
      accessorKey: 'visitor_profile_count',
      header: 'Visitors',
      cell: ({ row }) => row.original.visitor_profile_count ?? 0,
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) =>
        row.original.created_at ? formatDate(row.original.created_at) : 'N/A',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/leads/${row.original.id}`)}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const table = useReactTable({
    data: leads,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && leads.length === 0) {
    return (
      <TabsContent value="leads" className="space-y-4">
        <LoadingState label="Loading leads..." className="min-h-80" />
      </TabsContent>
    );
  }

  return (
    <TabsContent value="leads" className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-xl font-semibold">
          All Leads ({totalLeads})
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-center gap-2">
            <Input
              value={leadIdQuery}
              onChange={(e) => setLeadIdQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFindLeadById();
                if (e.key === 'Escape') setLeadIdQuery('');
              }}
              placeholder="Find by lead ID..."
              className="w-full sm:w-[300px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFindLeadById}
              disabled={!leadIdQuery.trim()}
              title="Open lead by ID"
            >
              <Search className="h-4 w-4 mr-1" />
              Find
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSearch(searchInput);
                if (e.key === 'Escape') {
                  setSearchInput('');
                  setSearch('');
                }
              }}
              placeholder="Search name, email, company..."
              className="w-full sm:w-[260px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearch(searchInput)}
            >
              Search
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Organization:</span>
            <OrganizationCombobox
              organizations={organizations}
              value={selectedOrg}
              onChange={setSelectedOrg}
            />
          </div>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No leads found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </TabsContent>
  );
};

export default LeadsTab;
